'use client';

import { useContext, useEffect, useState } from 'react';
import { getCookie } from 'cookies-next';
import { LineItem } from 'shared/types/cart';
import { CustomNarvarInsuranceProps, NarvarCart } from 'shared/types/cart/Narvar';
import { Money } from 'shared/types/product';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { RESPONSE_SUCCESS_MESSAGE } from '@/components/customercenter-ui/components/customer-detail/constants';
import { COOKIE_KEYS } from '@/constants/constant';
import { useCheckout } from '@/context/checkoutProvider';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import { useAccount, useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { CustomerDetailsPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import WorryFreeDeliveryModal from './worryFreeDeliveryModal';
import WorryFreeDeliveryRealTimeModal from './worryFreeDeliveryRealTimeModal';
import ContentStackImage from '../../../common/contentStackImage/contentStackImage';
import { Checkbox } from '../../../ui/checkbox';

export default function CustomNarvarInsurance({ cart, isCheckoutPage = false }: CustomNarvarInsuranceProps) {
  const { handleShippingInsurance } = useCart(false);
  const projectSettings: ProjectSettings = useProjSettings();
  const { setContextCart, setContactInformation, contactInformation, setSendPromotionalEmails } = useCheckout();
  const { getCustomerDetails } = useAccount();
  const { customerData, setCustomerData, setCustomerDetailsResponse } = useContext(CreditSummaryContext);
  const [sdkReady, setSdkReady] = useState(false);
  const [quoteData, setQuoteData] = useState<{ displayAmount: string; premium: number } | null>(null);
  const [jwsData, setJwsData] = useState<{ jws: string; expiresAt: number } | null>(null);
  const [isEligible, setIsEligible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRealtimeModal, setShowRealtimeModal] = useState(false);
  const [siteId, setSiteId] = useState<string>();
  const siteRetailerMonikers = projectSettings?.siteRetailerMonikers;
  const insuranceLineItem: LineItem | undefined = cart?.lineItems?.find((item: LineItem) => item?.isInsuranceItem);
  const shippingSurcharge = cart?.shippingSurcharge?.centAmount ?? 0;
  const totalShippingAmount = (cart?.actualShippingAmount?.centAmount ?? 0) + shippingSurcharge;
  const currency = cart?.total?.currencyCode ?? 'USD';
  const isNarvarEnabled = projectSettings?.isNarvarEnabled ?? false;
  const retailerMoniker = siteRetailerMonikers[siteId ?? 'ZBSite'];
  const zeroMoney: Money = {
    centAmount: 0,
    currencyCode: currency,
    fractionDigits: 2,
  };
  const divisionCustomer = customerData?.DivisionCustomers?.[0];
  const emailRecords = divisionCustomer?.EmailRecords ?? [];

  let adjAmount = 0;
  const currentAdjustment = cart?.currentAdjustment;
  if (currentAdjustment && Array.isArray(currentAdjustment) && currentAdjustment.length > 0) {
    const shippingDiscounts = currentAdjustment?.filter((adj: any) => adj.type === 'shippingDiscount');
    adjAmount = currentAdjustment?.at(-1)?.centAmount - (shippingDiscounts?.at(-1)?.discountedAmount ?? 0);
  }

  const profileEmail = emailRecords.find((emailRecord: any) => emailRecord?.EmailInfo?.emailAddress?.trim().length > 0)
    ?.EmailInfo?.emailAddress;

  const cartEmail = cart?.email;

  const orderHasEmail = contactInformation.email?.trim().length > 0;

  useEffect(() => {
    const emailSetOnceForSession = sessionStorage.getItem('emailSetOnceForSession') === 'true';
    if (!emailSetOnceForSession) {
      if (cartEmail) {
        setContactInformation({
          ...contactInformation,
          email: cartEmail,
        });
        setSendPromotionalEmails(false);
        sessionStorage.setItem('emailSetOnceForSession', 'true');
      } else if (profileEmail) {
        setContactInformation({
          ...contactInformation,
          email: profileEmail,
        });
        setSendPromotionalEmails(false);
        sessionStorage.setItem('emailSetOnceForSession', 'true');
      }
    }
    if (!isCheckoutPage) {
      setSendPromotionalEmails(false);
      if (!contactInformation?.email) {
        if (cartEmail) {
          setContactInformation({
            ...contactInformation,
            email: cartEmail,
          });
        } else if (profileEmail) {
          setContactInformation({
            ...contactInformation,
            email: profileEmail,
          });
        }
      }
    }
  }, [cartEmail, profileEmail, isCheckoutPage]);

  useEffect(() => {
    const cookieSiteId = getCookie(COOKIE_KEYS.selectedSite) ?? 'ZBSite';
    setSiteId(cookieSiteId);
  }, []);

  const filteredLineItems =
    cart?.lineItems?.filter((lineItem: LineItem) => {
      return !lineItem?.lineGroupId && !lineItem?.isInsuranceItem;
    }) ?? [];

  const digitalItems = cart?.lineItems?.filter((lineItem: LineItem) => {
    return lineItem?.lineGroupId || lineItem?.isInsuranceItem;
  });

  const digitalItemsTotal = digitalItems?.reduce(
    (total: Money, item: LineItem) => CurrencyHelpers.addCurrency(total, item.price),
    zeroMoney,
  );
  const subTotal = (cart?.subTotal?.centAmount ?? 0) - (digitalItemsTotal?.centAmount ?? 0);
  const narvarCart: NarvarCart = {
    items: filteredLineItems?.map((item: any) => ({
      sku: item.variant?.sku,
      qty: item.count,
      price: (item?.discountedPrice?.centAmount ?? item?.price?.centAmount ?? 0) * item?.count,
    })),
    subTotal: subTotal ?? 0,
    currency,
    shipTo: 'US',
    hasInsuranceLineItem: Boolean(insuranceLineItem),
    insuranceLineItemId: insuranceLineItem?.lineItemId,
    shippingAmount: totalShippingAmount ?? 0,
    discountAmount: (cart?.discountTotal?.centAmount ?? 0) + adjAmount,
    orderNumber: cart?.orderNumber,
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerData) {
        setCustomerData(customerData);
      } else {
        const current_CDP_Data = getCookie(COOKIE_KEYS.CDP_URL) ?? '';
        if (current_CDP_Data.length) {
          const data = JSON.parse(current_CDP_Data);
          getCustomerDetailsData(data);
        }
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [customerData]);

  const getCustomerDetailsData = async (data: any) => {
    const payload = {
      query: {
        customerId: data?.customerId,
        omsCustomerId: data?.omsCustomerId,
        divisionCode: data?.divisionCode,
        idName: data?.idName,
      },
    } as CustomerDetailsPayload;
    await getCustomerDetails(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        setCustomerData(response?.Customer);
        setCustomerDetailsResponse(response);
      }
    });
  };

  const handleShippingInsuranceEvent = async (
    action: string,
    price?: number,
    lineItemId?: string,
    jws?: string,
    expiresAt?: number,
    email?: string,
  ) => {
    let responseCart = cart;
    if (action === 'add' && price && !Boolean(insuranceLineItem)) {
      responseCart = await handleShippingInsurance({
        action,
        price,
        sku: projectSettings?.insuranceSku,
        jws,
        expiresAt,
        email,
      });
      if (email && email !== contactInformation?.email) {
        setContactInformation({
          ...contactInformation,
          email: email,
        });
      }
      if (isCheckoutPage) {
        setSendPromotionalEmails(false);
      }
    } else if (action === 'remove' && lineItemId && Boolean(insuranceLineItem)) {
      responseCart = await handleShippingInsurance({ action, lineItemId });
    } else if (action === 'update' && lineItemId && price && Boolean(insuranceLineItem)) {
      responseCart = await handleShippingInsurance({ action, lineItemId, price, jws, expiresAt });
      if (isCheckoutPage) {
        setSendPromotionalEmails(false);
      }
    }
    if (responseCart && isCheckoutPage) {
      setContextCart(responseCart);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !isNarvarEnabled || !siteId || !customerData) return;
    let unsubscribeReady: (() => void) | undefined;
    const interval = setInterval(() => {
      const SDK = window.Narvar?.ShippingProtection;
      if (!SDK) {
        return;
      }
      let omsCustomerId = '';
      const current_CDP_Data = getCookie(COOKIE_KEYS.CDP_URL) ?? '';
      if (current_CDP_Data.length) {
        const data = JSON.parse(current_CDP_Data);
        omsCustomerId = data?.omsCustomerId;
      }
      clearInterval(interval);
      SDK.init({
        retailerMoniker,
        placement: isCheckoutPage ? 'checkout' : 'cart',
        locale: 'en-US',
        sessionId: narvarCart?.orderNumber,
        ...(omsCustomerId && { customerId: omsCustomerId }),
      });
      if (SDK.isReady()) {
        setSdkReady(true);
      } else {
        unsubscribeReady = SDK.on('narvar:shipping-protection:state:ready', () => setSdkReady(true));
      }
    }, 100);
    return () => {
      clearInterval(interval);
      unsubscribeReady?.();
    };
  }, [retailerMoniker, isCheckoutPage, narvarCart?.orderNumber, isNarvarEnabled, siteId, customerData]);

  useEffect(() => {
    const handleQuoteAndAutoOptIn = async () => {
      if (isNarvarEnabled) {
        if (!sdkReady || typeof window === 'undefined' || !customerData || !siteId) return;
        setLoading(true);
        const cartPayload = {
          currency: 'USD',
          fees: narvarCart.shippingAmount ? [{ type: 'shipping', value: narvarCart.shippingAmount }] : [],
          items: narvarCart?.items?.map((item) => ({
            linePrice: item.price,
            sku: item.sku,
            quantity: item.qty,
          })),
          subtotal: narvarCart.subTotal,
          discount: narvarCart?.discountAmount,
          shipTo: 'US',
        };
        const result = await window.Narvar.ShippingProtection.getQuote(cartPayload);
        if (result?.success && result?.data?.eligible) {
          setIsEligible(true);
          const quote = result?.data?.quote;
          setQuoteData({
            displayAmount: quote?.displayAmount,
            premium: quote?.premium,
          });
          let jws = '';
          let expiresAt: any = undefined;
          if (isCheckoutPage && quote?.signature?.jws) {
            jws = quote?.signature?.jws;
            expiresAt = quote?.signature?.expires_at;
            setJwsData({ jws: quote?.signature?.jws, expiresAt: quote?.signature?.expires_at });
          }
          const openedOnceForOrder = sessionStorage.getItem('openedOnceForOrder') === 'true';
          if (!narvarCart.hasInsuranceLineItem) {
            if (!openedOnceForOrder && isCheckoutPage) {
              setShowRealtimeModal(true);
              sessionStorage.setItem('openedOnceForOrder', 'true');
            }
          } else {
            await handleShippingInsuranceEvent(
              'update',
              quote?.premium,
              narvarCart?.insuranceLineItemId,
              jws,
              expiresAt,
            );
          }
        } else {
          if (narvarCart.hasInsuranceLineItem) {
            await window.Narvar.ShippingProtection.removeProtection();
            await handleShippingInsuranceEvent('remove', undefined, narvarCart?.insuranceLineItemId);
          }
          setIsEligible(false);
          setQuoteData(null);
        }
        setLoading(false);
      } else {
        if (narvarCart.hasInsuranceLineItem) {
          await handleShippingInsuranceEvent('remove', undefined, narvarCart?.insuranceLineItemId);
        }
      }
    };
    handleQuoteAndAutoOptIn();
  }, [
    sdkReady,
    narvarCart?.subTotal,
    narvarCart?.shippingAmount,
    narvarCart?.orderNumber,
    narvarCart?.discountAmount,
    isNarvarEnabled,
    siteId,
    customerData,
  ]);

  const handleToggleChange = async (event: any) => {
    if (!quoteData || loading || !customerData || !siteId) return;
    setLoading(true);
    if (event) {
      if (orderHasEmail) {
        await window.Narvar.ShippingProtection.addProtection();
        await handleShippingInsuranceEvent('add', quoteData.premium, undefined, jwsData?.jws, jwsData?.expiresAt);
      } else {
        setShowRealtimeModal(true);
      }
    } else {
      await window.Narvar.ShippingProtection.removeProtection();
      await handleShippingInsuranceEvent('remove', undefined, narvarCart.insuranceLineItemId);
    }
    setLoading(false);
  };

  const handleRealtimeModalAdd = async (email?: string) => {
    setLoading(true);
    await window.Narvar.ShippingProtection.addProtection();
    await handleShippingInsuranceEvent('add', quoteData?.premium, undefined, jwsData?.jws, jwsData?.expiresAt, email);
    setLoading(false);
    setShowRealtimeModal(false);
  };

  const handleRealtimeModalDecline = async () => {
    setLoading(true);
    if (narvarCart.hasInsuranceLineItem) {
      await window.Narvar.ShippingProtection.removeProtection();
      await handleShippingInsuranceEvent('remove', undefined, narvarCart?.insuranceLineItemId);
    }
    setLoading(false);
    setShowRealtimeModal(false);
  };

  if (!isEligible || !quoteData || !isNarvarEnabled || !isCheckoutPage) return null;

  return (
    <>
      <section
        data-testid={`${isCheckoutPage ? 'section-shipping-insurance-checkout' : 'section-shipping-insurance-cart'}`}
        className="relative mt-5 overflow-hidden rounded-[4px] border border-silverGray bg-white px-2 py-5"
      >
        {loading && (
          <section
            aria-live="polite"
            aria-label="Loading"
            className="absolute inset-0 z-10 flex items-center justify-center rounded-[4px] bg-[rgba(255,255,255,0.75)]"
          >
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
          </section>
        )}
        <section className="flex items-start space-x-1 px-1">
          <label className="flex cursor-pointer px-[3px] text-base font-normal leading-6" htmlFor="worry_free_delivery">
            <Checkbox
              className="
              h-[18.65px] 
              w-[18.65px] 
              rounded-[4.39px] 
              border-[2.19px]
              border-black
              data-[state=checked]:border-black
              data-[state=checked]:bg-black
              data-[state=checked]:text-white
              "
              data-testid={`${
                isCheckoutPage ? 'button-checkout-shipping-insurance' : 'button-cart-shipping-insurance'
              }`}
              defaultChecked={narvarCart?.hasInsuranceLineItem}
              checked={narvarCart?.hasInsuranceLineItem}
              id="worry_free_delivery"
              onCheckedChange={(e) => handleToggleChange(e)}
              aria-label={`Click for Worry-Free Delivery }`}
              disabled={loading}
            />
            <span className="-mt-[1px] cursor-pointer pl-2 text-[13px] leading-[20px]">
              <strong>{mapLocaleToMeaningfulFormat().worryFreeDelivery}</strong> for {quoteData?.displayAmount}
            </span>
          </label>
          <div className="relative -top-0.5 inline-block">
            <WorryFreeDeliveryModal />
          </div>
        </section>
        <section className="px-2 text-[12px] leading-[16px] text-[#202223]">
          {mapLocaleToMeaningfulFormat().worryFreeDeliveryDesc}
        </section>
        <section className="mx-2 pt-1">
          <ContentStackImage
            src={projectSettings?.poweredBySeelImageURL ?? ''}
            imageWidth={64.77}
            imageHeight={11}
            className="h-[11px] w-[64.77px]"
          />
        </section>
      </section>
      <WorryFreeDeliveryRealTimeModal
        open={showRealtimeModal}
        setOpen={setShowRealtimeModal}
        insuranceItemPrice={quoteData?.displayAmount}
        orderHasEmail={orderHasEmail}
        onAdd={handleRealtimeModalAdd}
        onDecline={handleRealtimeModalDecline}
        loading={loading}
      />
    </>
  );
}
